import { LoadingOverlay, ScrollArea } from '@mantine/core';

import { useNavigate, useParams } from 'react-router-dom';

import { useForm } from '@mantine/form';

import { type FileWithPath } from '@mantine/dropzone';

import { useCallback, useState } from 'react';

import useEnsisMutation from '../../hooks/useEnsisMutation';
import { INVALID_FILE_ERROR_MESSAGE } from '../../utils/apiUtils';
import { DEFAULT_FAILURE_MESSAGE, showFailureNotification } from '../../utils/mantineUtils';
import { logAnalyticsEvent } from '../../utils/analyticsUtils';

import WizardContent from './WizardContent';
import { InitializeProposalForm } from '../../components';

interface FormValues {
  files: FileWithPath[]
  proposalName: string
  additionalContext: string
}

interface Props {
  onDone: () => void
}

const CreateProposal: React.FC<Props> = (props: Props) => {
  const { onDone } = props;
  const [skipFileUpload, setSkipFileUpload] = useState(false);

  const validateFiles = useCallback((files: FileWithPath[]) => {
    if (skipFileUpload) {
      return null;
    }
    if (files === null || files.length === 0) {
      return 'Please select file(s) to upload';
    }
    return null;
  }, [skipFileUpload]);

  const { organization } = useParams();
  const form = useForm({
    initialValues: {
      files: [] as FileWithPath[],
      proposalName: '',
      additionalContext: ''
    },
    validate: {
      files: validateFiles,
      proposalName: (value: string) => (value.trim().length < 1 ? 'Proposal name is required' : null)
    }
  });
  const navigate = useNavigate();
  const submitProposalMutation = useEnsisMutation(
    '/app/proposals',
    {
      contentType: 'multipart/form-data',
      requestType: 'post',
      showSuccessMessage: false,
      showFailureMessage: false,
      queryKeysToInvalidate: ['/app/proposals'],
      onSuccess: (data) => {
        navigate(`/org/${organization}/proposals/create-proposal/${data.uid}`);
        onDone();
      },
      onError: (error) => {
        let errorMessage = DEFAULT_FAILURE_MESSAGE;
        if (error?.response?.status === 422) {
          errorMessage = INVALID_FILE_ERROR_MESSAGE;
        }
        showFailureNotification(errorMessage);
      }
    }
  );

  const onSubmitForm = useCallback((values: FormValues) => {
    logAnalyticsEvent('CreateProposal');
    submitProposalMutation.mutate({
      name: values.proposalName,
      files: values.files as unknown as string[],
      additional_context: values.additionalContext,
      organization_slug: organization ?? '',
      generate_fast_draft: false
    });
  }, [submitProposalMutation.mutate]);

  return (
    <WizardContent
      flex={1}
      titleCopy="Let's get started"
      subtitleCopy="First give your proposal a name, then import your opportunity file(s)"
      nextButtonDisabled={!form.isValid()}
      onClickNext={() => {
        onSubmitForm(form.values);
      }}
    >
      <LoadingOverlay visible={submitProposalMutation.isPending} />
      <ScrollArea.Autosize mah={'100%'}>
        <InitializeProposalForm
          form={form}
          skipFileUpload={skipFileUpload}
          setSkipFileUpload={setSkipFileUpload}
          mx={10}
        />
      </ScrollArea.Autosize>
    </WizardContent>
  );
};

export default CreateProposal;
